// Correos tipo (Backoffice → Plataforma → Correos): editable e-mails the
// platform superadmin sends to a lead of tallerza.cl or to a workshop's admin.
// The texts are plain text with markers ({{nombre}}…), rendered by
// correoHtml.ts (same render in the backoffice preview and in the Function).
//
// Markers are a closed list. Those of the recipient and the links are always
// resolved by the server (never taken from the request); the free ones are
// filled by the superadmin when sending. Base templates (PLANTILLAS_BASE) are
// the playbook's (docs/comercial/proceso-contactos.md; a test keeps them
// equal): a base template without a stored document shows its factory text.
// Correo A stays in "Invitar a la prueba" (it carries the temporary password).
import { z } from 'zod';
import {
  ADMIN_URL,
  APP_URL,
  LINEA_IOS,
  PLAY_URL,
  correoLibre,
  leerImagenCorreo,
  unaLinea,
  type Plantilla,
} from './correoHtml.ts';
import { ID_TALLER } from './invitacion.ts';

/** Stored templates: `correosTipo/{id}` (Admin SDK only; superadmin reads). */
export const COLECCION_CORREOS_TIPO = 'correosTipo';
/** The links the templates use (Admin SDK only; superadmin reads). */
export const RUTA_ENLACES_CORREO = 'configPlataforma/correos';

/** Resolved by the server from the recipient and the platform's links. */
export const MARCADORES_SERVIDOR = [
  'nombre',
  'taller',
  'correo_admin',
  'enlace_docs',
  'enlace_video',
  'enlace_manual',
  'enlace_app',
  'enlace_admin',
  'enlace_play',
] as const;
/** Filled by the superadmin when sending (the playbook's free markers). */
export const MARCADORES_LIBRES = [
  'autos_mes',
  'plan',
  'precio',
  'detalle_oferta',
  'fecha_fin_prueba',
  'motivo',
  'horario_1',
  'horario_2',
  'horario_3',
] as const;
export type MarcadorServidor = (typeof MARCADORES_SERVIDOR)[number];
export type MarcadorLibre = (typeof MARCADORES_LIBRES)[number];
export type Marcador = MarcadorServidor | MarcadorLibre;
export const MARCADORES: readonly Marcador[] = [...MARCADORES_SERVIDOR, ...MARCADORES_LIBRES];

/** Label of each marker for the editor's chips. */
export const ETIQUETAS_MARCADORES: Record<Marcador, string> = {
  nombre: 'Nombre de la persona',
  taller: 'Nombre del taller',
  correo_admin: 'Correo de su cuenta',
  enlace_docs: 'Enlace a la guía',
  enlace_video: 'Enlace al video',
  enlace_manual: 'Enlace al manual',
  enlace_app: 'Enlace a la app',
  enlace_admin: 'Enlace al backoffice',
  enlace_play: 'Enlace a Google Play',
  autos_mes: 'Autos al mes',
  plan: 'Plan',
  precio: 'Precio',
  detalle_oferta: 'Detalle de la oferta',
  fecha_fin_prueba: 'Fin de la prueba',
  motivo: 'Motivo',
  horario_1: 'Horario 1',
  horario_2: 'Horario 2',
  horario_3: 'Horario 3',
};

const esMarcador = (m: string): m is Marcador => (MARCADORES as readonly string[]).includes(m);
export const esMarcadorLibre = (m: string): m is MarcadorLibre =>
  (MARCADORES_LIBRES as readonly string[]).includes(m);

const MARCA = /\{\{([^{}]*)\}\}/g;

/** Markers used by a text, in order of first appearance, without repeats. */
export function marcadoresDe(texto: string): string[] {
  return [...new Set([...texto.matchAll(MARCA)].map((m) => m[1] ?? ''))];
}

/** What is wrong with a template text (subject or body), or null. */
export function revisarTextoCorreo(texto: string): string | null {
  const desconocido = marcadoresDe(texto).find((m) => !esMarcador(m));
  if (desconocido !== undefined) return `Marcador desconocido: {{${desconocido}}}.`;
  if (/\{\{|\}\}/.test(texto.replace(MARCA, '')))
    return 'Hay un marcador mal escrito: se escriben así, {{nombre}}.';
  // An image from any other site would load a tracker or a lure in the
  // recipient's mail client: only tallerza.cl, and always with its alt text.
  for (const linea of texto.split(/\r\n?|\n/)) {
    const imagen = leerImagenCorreo(linea);
    if (imagen && 'error' in imagen) return imagen.error;
  }
  return null;
}

/** Values to put in place of the markers ('' or absent = no value). */
export type ValoresCorreo = Partial<Record<Marcador, string>>;

/** Markers of `texto` with no value (the greeting's {{nombre}} may be empty). */
export function marcadoresSinValor(texto: string, valores: ValoresCorreo): Marcador[] {
  return marcadoresDe(sinNombreVacio(texto, valores)).filter(
    (m): m is Marcador => esMarcador(m) && !limpiar(valores[m]),
  );
}

export class ErrorPlantillaCorreo extends Error {
  readonly faltan: Marcador[];
  constructor(faltan: Marcador[]) {
    super(`Falta completar: ${faltan.map((m) => `{{${m}}}`).join(', ')}.`);
    this.faltan = faltan;
  }
}

/**
 * Values go in as one line (no control characters) and without square
 * brackets: a lead's name typed in a public form can never turn into a
 * `[button](link)` paragraph.
 */
const limpiar = (v: string | undefined) => unaLinea(v ?? '', 500).replace(/[[\]]/g, '');

/**
 * Only the platform's links (enlace_*) may become a link in the e-mail: in
 * any other value (a lead's name or workshop from the public form, a free
 * marker) the scheme is broken so the render never links it.
 */
const valorDe = (k: Marcador, v: string | undefined) =>
  k.startsWith('enlace_') ? limpiar(v) : limpiar(v).replace(/\b(https?):\/\//gi, '$1: //');

/** A lead without a name: "Hola, {{nombre}}:" reads "Hola:". */
const sinNombreVacio = (texto: string, valores: ValoresCorreo) =>
  limpiar(valores.nombre) ? texto : texto.replace(/,\s*\{\{nombre\}\}/g, '');

/**
 * The text with every marker in place. Throws ErrorPlantillaCorreo when one
 * has no value (an empty video link, a free marker left blank). Single pass:
 * a value that contains `{{…}}` is not expanded again.
 */
export function resolverPlantilla(texto: string, valores: ValoresCorreo): string {
  const faltan = marcadoresSinValor(texto, valores);
  if (faltan.length) throw new ErrorPlantillaCorreo(faltan);
  return sinNombreVacio(texto, valores).replace(MARCA, (m, k: string) =>
    esMarcador(k) ? valorDe(k, valores[k]) : m,
  );
}

// ---- Links ----

/** https URL whose every character the e-mail render links (correoHtml.ts). */
const URL_CORREO = /^https:\/\/[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+(\/[A-Za-z0-9.\-/_?=#%~+:&]*)?$/;
const enlace = z.union([
  z.literal(''),
  z.string().trim().max(500).regex(URL_CORREO, 'Debe ser un enlace https:// completo'),
]);

/** `configPlataforma/correos`. Empty = not published yet (the send fails if a template uses it). */
export const esquemaEnlacesCorreo = z.strictObject({
  docs: enlace,
  video: enlace,
  manual: enlace,
});
export type EnlacesCorreo = z.infer<typeof esquemaEnlacesCorreo>;
export const ENLACES_VACIOS: EnlacesCorreo = { docs: '', video: '', manual: '' };

/** The recipient as the server reads it (lead or workshop admin). */
export type DestinatarioCorreo = { nombre: string; taller: string; correoAdmin: string };

/** Values of the server markers, the same in the Function and the preview. */
export function valoresServidor(
  d: DestinatarioCorreo,
  enlaces: EnlacesCorreo,
): Record<MarcadorServidor, string> {
  return {
    nombre: d.nombre,
    taller: d.taller,
    correo_admin: d.correoAdmin,
    enlace_docs: enlaces.docs,
    enlace_video: enlaces.video,
    enlace_manual: enlaces.manual,
    enlace_app: APP_URL,
    enlace_admin: ADMIN_URL,
    enlace_play: PLAY_URL,
  };
}

// ---- Templates ----

export const MAX_CUERPO_CORREO = 12_000;

export type TipoDestinoCorreo = 'contacto' | 'taller';

/** Footer line: why the person gets the e-mail. */
export const pieCorreoTipo = (tipo: TipoDestinoCorreo) =>
  tipo === 'contacto'
    ? 'Te escribimos porque pediste conocer Tallerza en tallerza.cl.'
    : 'Te escribimos porque tu taller usa Tallerza.';

/**
 * The e-mail as it is sent (and previewed): markers resolved, brand frame.
 * Throws ErrorPlantillaCorreo when a marker has no value.
 */
export function renderCorreoTipo(p: {
  asunto: string;
  cuerpo: string;
  valores: ValoresCorreo;
  tipo: TipoDestinoCorreo;
}): Plantilla {
  const faltan = [
    ...new Set([
      ...marcadoresSinValor(p.asunto, p.valores),
      ...marcadoresSinValor(p.cuerpo, p.valores),
    ]),
  ];
  if (faltan.length) throw new ErrorPlantillaCorreo(faltan);
  const asunto = resolverPlantilla(p.asunto, p.valores);
  return correoLibre({
    asunto,
    texto: resolverPlantilla(p.cuerpo, p.valores),
    resumen: asunto,
    pie: pieCorreoTipo(p.tipo),
  });
}

const textoPlantilla = (min: number, max: number) =>
  z
    .string()
    .trim()
    .min(min)
    .max(max)
    .superRefine((v, ctx) => {
      const error = revisarTextoCorreo(v);
      if (error) ctx.addIssue({ code: 'custom', message: error });
    });

const campos = {
  nombre: z.string().trim().min(2).max(60),
  asunto: textoPlantilla(3, 200),
  cuerpo: textoPlantilla(1, MAX_CUERPO_CORREO),
  archivada: z.boolean(),
};
export const esquemaPlantillaCorreo = z.strictObject(campos);
export type PlantillaCorreo = z.infer<typeof esquemaPlantillaCorreo>;
/** What `correosTipo/{id}` holds (server stamps added). */
export type PlantillaCorreoGuardada<F = unknown> = PlantillaCorreo & {
  actualizadoEn: F;
  actualizadoPor: string;
};

export const IDS_PLANTILLAS_BASE = [
  'conoce-tallerza',
  'bienvenida',
  'novedades',
  'despues-demo',
  'sin-respuesta',
  'cierre',
] as const;
export type IdPlantillaBase = (typeof IDS_PLANTILLAS_BASE)[number];
export const esPlantillaBase = (id: string): id is IdPlantillaBase =>
  (IDS_PLANTILLAS_BASE as readonly string[]).includes(id);

const FIRMA = `Saludos,
Equipo Tallerza
contacto@tallerza.cl · WhatsApp +56 9 9487 6263`;

/** Pictures of the e-mails, served by the landing (apps/landing/public/correos/). */
const IMAGEN = (nombre: string) => `https://tallerza.cl/correos/${nombre}.webp`;

/**
 * Factory texts: Conoce Tallerza, Bienvenida, Novedades, B, C and D of the
 * playbook (a test compares them).
 */
export const PLANTILLAS_BASE: Record<IdPlantillaBase, PlantillaCorreo> = {
  'conoce-tallerza': {
    nombre: 'Conoce Tallerza',
    asunto: 'Así funciona Tallerza en un taller como {{taller}}',
    cuerpo: `Hola, {{nombre}}:

Gracias por tu interés en Tallerza. Antes de darte los accesos, te contamos en corto cómo funciona.

Tallerza ordena el trabajo de un taller de desabolladura y pintura. Cada auto entra con fotos, el estado de sus 15 zonas y la firma del cliente. Siempre sabes en qué va cada uno y, al entregarlo, queda claro qué daño ya traía.

![Gemelo digital del auto con sus 15 zonas marcadas](${IMAGEN('gemelo')})

**Cómo es un día con Tallerza**

1. **Ingreso**: en la tablet o el teléfono marcas cada zona, sacas fotos de los daños y el cliente firma.
2. **Tablero de fases**: en el computador ves en qué fase va cada auto y cuáles se atrasan.
3. **Entrega**: el PDF compara el auto con su ingreso y el cliente firma conforme.

Para verlo con calma:

[Ver la guía]({{enlace_docs}})

[Ver el video de 2 minutos]({{enlace_video}})

[Descargar el manual]({{enlace_manual}})

La app de inspección ya está en Google Play, para teléfonos y tablets Android:

[Descargar en Google Play]({{enlace_play}})

${LINEA_IOS}

Si quieres verlo funcionando o tienes dudas, escríbenos por WhatsApp al +56 9 9487 6263 o a contacto@tallerza.cl.

Saludos,
Equipo Tallerza`,
    archivada: false,
  },
  novedades: {
    nombre: 'Novedades',
    asunto: 'Novedades de Tallerza para {{taller}}',
    cuerpo: `Hola, {{nombre}}: desde que abrimos {{taller}} en Tallerza sumamos varias cosas que vale la pena que conozcas.

**La app ya está en Google Play**

![Tallerza en Google Play](${IMAGEN('play')})

Instálala en los teléfonos y tablets Android del taller.

[Descargar en Google Play]({{enlace_play}})

${LINEA_IOS}

**Una app de inspección más rápida de llenar**

![Accesorios en la app de inspección, con letra grande y el avance de los pasos](${IMAGEN('accesorios')})

La rediseñamos con letra grande y pasos con su progreso a la vista, para que cada ingreso tome menos tiempo.

**Un PDF nuevo, con el sello del taller**

![PDF de la inspección con el sello del taller](${IMAGEN('pdf')})

El PDF lleva el sello de {{taller}}. En la entrega, el acta se compara contra el ingreso: se ve qué daño ya venía y cuál es nuevo.

**Avisos legales de la firma, a tu medida**

![Firma del cliente bajo el aviso legal del taller](${IMAGEN('firma')})

Edita el texto que acepta el cliente al firmar en Configuración → Avisos legales. Cada inspección firmada guarda el texto que se aceptó.

**Ficha del cliente por RUT**

Escribe el RUT y, en la próxima visita, los datos del cliente se completan solos.

**Catálogo chileno, también sin señal**

Marcas, modelos, colores y aseguradoras de Chile, listos para elegir aunque en el taller no haya señal.

**Tablet compartida**

![Inicio de la app en la tablet compartida del taller](${IMAGEN('inicio')})

Enlaza una tablet del taller con un código. Cada persona toca su nombre en «¿Quién eres?» y entra con su PIN.

**Ayuda en tallerza.cl/docs**

![Centro de ayuda de Tallerza](${IMAGEN('docs')})

La guía tiene buscador, video y manual en PDF.

[Ver la guía]({{enlace_docs}})

¿Te lo mostramos? Son 30 minutos, por videollamada o en tu taller. Responde este correo con un día y una hora, o escríbenos por WhatsApp al +56 9 9487 6263.

${FIRMA}`,
    archivada: false,
  },
  bienvenida: {
    nombre: 'Bienvenida',
    asunto: 'Te damos la bienvenida a Tallerza, {{taller}}',
    cuerpo: `Hola, {{nombre}}:

Gracias por elegir Tallerza para {{taller}}. Te dejamos todo lo que necesitas para partir.

**Tus accesos**

- Backoffice (computador): {{enlace_admin}}
- App de inspección (teléfono o tablet): {{enlace_app}}
- Correo de tu cuenta: {{correo_admin}}

Para instalar la app en teléfonos y tablets Android:

[Descargar en Google Play]({{enlace_play}})

${LINEA_IOS}

Si no recuerdas tu contraseña, toca «Olvidé mi contraseña» al entrar y te llegará un enlace para crear una nueva.

**Para aprender a usarlo**

La guía explica paso a paso cómo hacer un ingreso con fotos y firma, mover los autos en el tablero y hacer la entrega:

[Ver la guía]({{enlace_docs}})

En este video te mostramos una inspección completa en pocos minutos:

[Ver el video]({{enlace_video}})

Y el manual en PDF, para tenerlo a mano o imprimirlo:

[Descargar el manual]({{enlace_manual}})

Si tienes cualquier duda, responde este correo o escríbenos por WhatsApp.

${FIRMA}`,
    archivada: false,
  },
  'despues-demo': {
    nombre: 'Después de la demo (B)',
    asunto: 'Resumen de la demo de Tallerza y plan para {{taller}}',
    cuerpo: `Hola, {{nombre}}:

Gracias por tu tiempo. Un resumen de lo que vimos:

- Cada auto se recibe con el estado de sus 15 zonas, fotos de cada daño, accesorios, combustible y la firma del cliente, en un PDF numerado que no se puede editar después.
- El tablero muestra en qué fase está cada auto y cuáles se atrasan.
- La entrega se compara con el ingreso: queda claro qué daño ya venía y cuál es nuevo.

Con {{autos_mes}} autos al mes, el plan que te conviene es **{{plan}}**: {{precio}} + IVA. {{detalle_oferta}} Todos los planes incluyen todas las funciones y usuarios sin límite.

Tu prueba sigue hasta el {{fecha_fin_prueba}}. Si decides seguir, no cambia nada: sigues en el mismo taller con todo lo que ya cargaste, y solo quitamos el auto de ejemplo.

¿Te parece si lo conversamos esta semana?

${FIRMA}`,
    archivada: false,
  },
  'sin-respuesta': {
    nombre: 'Sin respuesta (C)',
    asunto: '¿Pudiste mirar Tallerza?',
    cuerpo: `Hola, {{nombre}}:

Te escribimos hace una semana con los accesos a {{taller}} en Tallerza. Sabemos que en el taller el tiempo no sobra, así que solo queríamos saber si pudiste entrar o si prefieres que te lo mostremos en 30 minutos.

Si te sirve, responde este correo con un día y una hora, o escríbenos por WhatsApp al +56 9 9487 6263. Si no es el momento, también nos sirve saberlo.

Saludos,
Equipo Tallerza`,
    archivada: false,
  },
  cierre: {
    nombre: 'Cierre (D)',
    asunto: 'Cerramos tu prueba de Tallerza',
    cuerpo: `Hola, {{nombre}}:

Gracias por darte el tiempo de probar Tallerza. Entendemos que por ahora no es el momento ({{motivo}}).

Como dicen las condiciones de la prueba, dentro de 15 días eliminamos los datos de {{taller}} en Tallerza. Si quieres una exportación de lo que cargaste, respóndenos este correo y te la enviamos antes. Las fotos y los PDF de las inspecciones quedan bloqueados, sin acceso de nadie, hasta cumplir 1 año desde que se tomaron, y ahí se eliminan.

Si más adelante quieres retomarlo, nos escribes a contacto@tallerza.cl.

Saludos,
Equipo Tallerza`,
    archivada: false,
  },
};

// ---- Callable contracts ----

/** Firestore auto id or a base template id. */
const idPlantilla = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);
const idDoc = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

/**
 * `guardarPlantillaCorreo`: without `id` creates a new template; with `id`
 * replaces a stored one or a base one (base ids may be saved without a
 * document yet). Archive = save with `archivada: true` (nothing is deleted).
 */
export const esquemaGuardarPlantillaCorreo = z.strictObject({
  id: idPlantilla.optional(),
  ...campos,
});
export type GuardarPlantillaCorreo = z.infer<typeof esquemaGuardarPlantillaCorreo>;
export type RespuestaGuardarPlantillaCorreo = { id: string };

/** `guardarEnlacesCorreo`: the whole set of links is replaced. */
export const esquemaGuardarEnlacesCorreo = esquemaEnlacesCorreo;

/** Who gets the e-mail: a lead (`contactos/{id}`) or a workshop's admin. */
export const esquemaDestinoCorreo = z.discriminatedUnion('tipo', [
  z.strictObject({ tipo: z.literal('contacto'), id: idDoc }),
  z.strictObject({ tipo: z.literal('taller'), tallerId: z.string().regex(ID_TALLER) }),
]);
export type DestinoCorreo = z.infer<typeof esquemaDestinoCorreo>;

/**
 * `enviarCorreoTipo`: `asunto` and `cuerpo` as the superadmin left them (with
 * markers); `valores` only for the free markers (a server marker in it is
 * rejected). The recipient's address, name, workshop and the links are
 * resolved by the server.
 */
export const esquemaEnviarCorreoTipo = z.strictObject({
  plantillaId: idPlantilla,
  destino: esquemaDestinoCorreo,
  asunto: textoPlantilla(3, 200),
  cuerpo: textoPlantilla(1, MAX_CUERPO_CORREO),
  valores: z.partialRecord(z.enum(MARCADORES_LIBRES), z.string().trim().max(500)).default({}),
});
export type EnviarCorreoTipo = z.input<typeof esquemaEnviarCorreoTipo>;
export type RespuestaEnviarCorreoTipo = { resultado: 'enviado' };

/** Sends of correos tipo in 24 h, all recipients together (bounds a stolen session). */
export const LIMITE_CORREOS_TIPO_DIA = 50;
/** Minimum time between two correos tipo to the same recipient (double click). */
export const PAUSA_CORREO_TIPO_MS = 60_000;
/** Entries kept in a lead's `correosEnviados` (newest last). */
export const MAX_HISTORIAL_CORREOS = 20;

/** One entry of `contactos/{id}.correosEnviados` (server only). */
export type EnvioCorreoTipo = {
  /** Epoch ms. */
  en: number;
  plantillaId: string;
  /** Subject as sent. */
  asunto: string;
  /** uid of the superadmin. */
  por: string;
};
